import { useCallback } from 'react'

import emDash from '~/components/emdash'

import { type TemperatureUnit, isTemperatureUnit } from '../types/units'
import { useNumberFormatter } from './number'
import type { FormatterHook } from './types'
import { combineOptions } from './types'
import type { UnitsFormat } from './units'
import { formatUnitDesc, useUnitsFormatter } from './units'

export const temperatureFormats: UnitsFormat<TemperatureUnit> = {
  celsius: {
    long: 'Celsius',
    short: '°C'
  },
  fahrenheit: {
    long: 'Fahrenheit',
    short: '°F'
  }
}

export const formatTemperatureUnitsDesc = formatUnitDesc(temperatureFormats)

const defaultOptions = {
  longForm: false,
  sigFigs: 1,
  units: 'celsius',
  omitUnits: false,
  invalidStr: emDash
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '' }

export const useTemperatureFormatter: FormatterHook = () => {
  const formatNumber = useNumberFormatter()
  const formatUnits = useUnitsFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: temperature,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      const temperatureUnits = units.toLowerCase()
      if (!isTemperatureUnit(temperatureUnits)) return invalidStr
      if (typeof temperature !== 'string' && typeof temperature !== 'number') return invalidStr

      // Coerce to number
      const temperatureNum = parseFloat(`${temperature}`)
      if (isNaN(temperatureNum)) return invalidStr

      const temperatureStr = formatNumber(temperatureNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return temperatureStr

      const formattedUnits = formatUnits.temperature(temperatureUnits, longForm ? 'long' : 'short')
      const joinChar = longForm ? ' ' : ''
      return [temperatureStr, formattedUnits].join(joinChar)
    },
    [formatNumber, formatUnits]
  )
}
